import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { APP_RELOAD, RELOAD_LANDING_ROUTE } from './app-reload';

const TOKEN_EXCHANGE_GRANT_TYPE = 'urn:ietf:params:oauth:grant-type:token-exchange';
const ACCESS_TOKEN_TYPE = 'urn:ietf:params:oauth:token-type:access_token';
const STORAGE_KEY = 'idrportalapp.impersonation';

interface TokenExchangeResponse {
  access_token: string;
}

interface ImpersonationState {
  readonly accessToken: string;
  readonly targetName: string;
  readonly impersonatorName: string;
}

/**
 * IT impersonation via ForthrightIdentityBroker's RFC 8693 token exchange (TokenController.cs) —
 * ported from The-Desk's ImpersonationService; see that repo's
 * docs/impersonation-guide.md for the full design.
 *
 * State persists to sessionStorage (not localStorage — still gone once the tab/browser actually
 * closes) so an accidental refresh doesn't silently drop it; the token's own 15-minute server-side
 * lifetime (TokenController.cs) is what actually bounds exposure, not this storage choice.
 *
 * This service does NOT touch OidcSecurityService's own session at all — the impersonator's real
 * login is untouched throughout. impersonation.interceptor.ts is what actually makes API calls
 * use this token instead of the real one while active.
 *
 * Both start() and stop() finish by reloading the whole app (APP_RELOAD, app-reload.ts) so that
 * all per-user state (rights, the current-user httpResource) is re-derived from scratch rather
 * than leaking across identity switches.
 *
 * stopForLogout() is the exception: signing out is already leaving the page under its own power,
 * so it ends impersonation without the reload. See its own comment.
 */
@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  private readonly http = inject(HttpClient);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly appReload = inject(APP_RELOAD);

  private readonly state = signal<ImpersonationState | null>(readPersistedState());

  readonly active = computed(() => this.state() !== null);
  readonly accessToken = computed(() => this.state()?.accessToken ?? null);
  readonly targetName = computed(() => this.state()?.targetName ?? null);
  readonly impersonatorName = computed(() => this.state()?.impersonatorName ?? null);
  readonly error = signal<string | null>(null);

  /**
   * The SecUser id this app is currently acting as, or null when not impersonating. Read off the
   * exchanged token's own sec_user_id claim rather than the id passed to start(), so it survives a
   * page reload (hydration only keeps the token) and always matches what IDR.ExternalApi resolves
   * the request to.
   */
  readonly targetSecUserId = computed(() => {
    const token = this.accessToken();
    if (!token) {
      return null;
    }

    const secUserId = Number(decodeAccessTokenClaims(token)['sec_user_id']);
    return Number.isNaN(secUserId) ? null : secUserId;
  });

  /**
   * The target's legacy CMS WebUserId, or null when the token has no `cms_web_user_id` claim
   * (the broker only sets it when the target's SecUser.CMSWebUserID has a value) or there's no
   * impersonation token at all.
   */
  readonly targetCmsWebUserId = computed(() => {
    const token = this.accessToken();
    if (!token) {
      return null;
    }

    const cmsWebUserId = Number(decodeAccessTokenClaims(token)['cms_web_user_id']);
    return Number.isNaN(cmsWebUserId) ? null : cmsWebUserId;
  });

  // No application_name in the exchange body — the broker resolves it from client_id's own
  // configured default (idrportalapp -> "IDR"), which is already sent below.
  async start(targetSecUserId: number): Promise<void> {
    this.error.set(null);

    const subjectToken = await firstValueFrom(this.oidcSecurityService.getAccessToken());
    const impersonatorClaims = this.oidcSecurityService.userData().userData as Record<string, unknown> | null;
    const impersonatorName = impersonatorClaims?.['name'] as string | undefined;

    const body = new URLSearchParams({
      grant_type: TOKEN_EXCHANGE_GRANT_TYPE,
      subject_token: subjectToken,
      subject_token_type: ACCESS_TOKEN_TYPE,
      target_sec_user_id: String(targetSecUserId),
      client_id: 'idrportalapp',
    });

    try {
      const response = await firstValueFrom(
        this.http.post<TokenExchangeResponse>(`${environment.brokerAuthority}/connect/token`, body.toString(), {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        }),
      );

      const targetName = (decodeAccessTokenClaims(response.access_token)['name'] as string | undefined) ?? `SecUserId ${targetSecUserId}`;

      const newState: ImpersonationState = {
        accessToken: response.access_token,
        targetName,
        impersonatorName: impersonatorName ?? 'you',
      };

      this.state.set(newState);
      writePersistedState(newState);

      // Persist first, then reload: the restored state is what makes the reloaded app come back up
      // already impersonating (readPersistedState above), with rights and identity re-derived from
      // the target's token. Landing on the default route rather than the current URL on purpose.
      this.appReload(RELOAD_LANDING_ROUTE);
    } catch (err: unknown) {
      this.error.set(err instanceof HttpErrorResponse && typeof err.error?.error_description === 'string' ? err.error.error_description : 'Failed to start impersonation.');
    }
  }

  /**
   * Ends impersonation and returns the impersonator to their own app — what the banner's Stop
   * button calls.
   *
   * Reloads for the same reason start() does: the page is still full of the target user's rights,
   * which must not survive back into the impersonator's own session. The reload is in the
   * `finally`: if the broker call fails, impersonation is over on this client either way, so the
   * app must not be left showing the target's data.
   */
  async stop(): Promise<void> {
    if (!this.accessToken()) {
      return;
    }

    try {
      await this.endImpersonation();
    } finally {
      this.appReload(RELOAD_LANDING_ROUTE);
    }
  }

  /**
   * Ends impersonation as part of signing out — same broker call and same clear as stop(), minus
   * the reload.
   *
   * Signing out has to end impersonation too, and the two are not independent. The exchanged token
   * lives in sessionStorage, which outlives a logout round trip in the same tab, so leaving it
   * behind would let the next person to sign in here come back up silently acting as the previous
   * target. And the broker's stop endpoint only accepts the impersonation token, so this has to
   * happen while that token is still the one going out — the caller awaits this before calling
   * logoff().
   *
   * No reload, because the caller is already navigating away (logoff() is a real redirect to the
   * broker); reloading here would race that redirect and could cancel it.
   *
   * Never rejects. endImpersonation() clears local state in its own `finally`, so a broker that
   * fails to acknowledge the stop still leaves this client not impersonating.
   */
  async stopForLogout(): Promise<void> {
    if (!this.accessToken()) {
      return;
    }

    try {
      await this.endImpersonation();
    } catch {
      // Deliberately swallowed — see above. Local state is already cleared.
    }
  }

  /**
   * Clears state only after the stop call settles (success or failure) — the interceptor reads
   * `accessToken()` to attach the impersonation token to this very request, so clearing first
   * would make the stop call itself go out under the real session instead, which the broker would
   * correctly reject as "not an impersonation token."
   */
  private async endImpersonation(): Promise<void> {
    try {
      await firstValueFrom(this.http.post(`${environment.brokerAuthority}/api/impersonate/stop`, null));
    } finally {
      this.state.set(null);
      clearPersistedState();
    }
  }
}

// No JWT library needed for this — just the standard base64url payload decode. Only ever called
// on a token this same service just received from the broker, never on untrusted input.
function decodeAccessTokenClaims(accessToken: string): Record<string, unknown> {
  const payload = accessToken.split('.')[1];
  const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(base64)) as Record<string, unknown>;
}

function isExpired(accessToken: string): boolean {
  const exp = decodeAccessTokenClaims(accessToken)['exp'] as number | undefined;
  // No exp claim at all is treated as expired — safer to drop an unparseable/unexpected token
  // than to restore something we can't actually vouch for the lifetime of.
  return typeof exp !== 'number' || Date.now() >= exp * 1000;
}

function readPersistedState(): ImpersonationState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as ImpersonationState;
    if (isExpired(parsed.accessToken)) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch {
    // Malformed entry (manual edit, format change across a deploy) — treat as absent rather than
    // throwing during service construction.
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

function writePersistedState(state: ImpersonationState): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function clearPersistedState(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}
